import { onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useAuth } from 'src/composables/app/useAuth'
import { useConfirmUpdate } from 'src/composables/app/useConfirmUpdate'
import { ensureFcmReady, notificationToken, useFCM } from 'src/composables/fcm/useFCM'

import { devMode } from 'src/config'
import {
  subscribeToTopic as fcmSubscribe,
  unsubscribeFromTopic as fcmUnsubscribe,
  unsubscribeFromAllTopics as fcmUnsubscribeAll,
} from 'src/services/api/firebase.service'
import { log } from 'src/services/app/log.service'
import { notifyService } from 'src/services/app/notify.service'
import { notificationService } from 'src/services/app/notification.service'
import {
  FCM_MESSAGE_TYPES,
  NOTIFICATION_TYPES,
  NOTIFICATION_TOPICS,
} from 'src/services/consts/api.const'
import { COMPANY_DETAILS_TABS } from 'src/services/consts/company.const'
import { ROUTES } from 'src/services/consts/routes.const'
import { DOM_EVENTS } from 'src/services/consts/app.const'

const activeSubscriptions = new Set()

export { notificationToken }

export const registerCompanyQuery = notificationService.registerCompanyQuery
export const registerProductQuery = notificationService.registerProductQuery
export const unregisterCompanyQuery = notificationService.unregisterCompanyQuery
export const unregisterProductQuery = notificationService.unregisterProductQuery

export function useFirebaseNotifications() {
  const route = useRoute()
  const router = useRouter()
  const { user } = useAuth()
  const { addUpdateNotification } = useConfirmUpdate()
  const { subscribeToMessages } = useFCM()

  const queryRegistry = notificationService.getQueryRegistry()
  let unsubscribeFromMessages = null
  let unsubscribeBackgroundMessages = null

  onMounted(() => {
    unsubscribeFromMessages = subscribeToMessages(handleMessage)
    unsubscribeBackgroundMessages = setupBackgroundMessageListener()
  })

  onBeforeUnmount(() => {
    if (unsubscribeFromMessages) {
      unsubscribeFromMessages()
      unsubscribeFromMessages = null
    }
    if (unsubscribeBackgroundMessages) {
      unsubscribeBackgroundMessages()
      unsubscribeBackgroundMessages = null
    }
  })

  watch(
    () => route.params.id,
    async (newId, oldId) => {
      if (newId !== oldId) {
        if (oldId) {
          await unsubscribeFromTopic(NOTIFICATION_TOPICS.COMPANY_EDIT(oldId))
          await unsubscribeFromTopic(NOTIFICATION_TOPICS.AI_ASSESSMENT(oldId))
        }

        if (newId) {
          await subscribeToTopic(NOTIFICATION_TOPICS.COMPANY_EDIT(newId))
          await subscribeToTopic(NOTIFICATION_TOPICS.AI_ASSESSMENT(newId))
        }
      }
    },
    { immediate: true },
  )

  watch(
    () => route.query.productId,
    async (newId, oldId) => {
      if (newId !== oldId) {
        const companyId = route.params.id

        if (!companyId) return

        if (oldId) {
          await unsubscribeFromTopic(NOTIFICATION_TOPICS.PRODUCT_EDIT(companyId, oldId))
          await unsubscribeFromTopic(NOTIFICATION_TOPICS.AI_ASSESSMENT(companyId, oldId))
        }

        if (newId) {
          await subscribeToTopic(NOTIFICATION_TOPICS.PRODUCT_EDIT(companyId, newId))
          await subscribeToTopic(NOTIFICATION_TOPICS.AI_ASSESSMENT(companyId, newId))
        }
      }
    },
    { immediate: true },
  )

  async function subscribeToTopic(topic) {
    try {
      const token = notificationToken.value || (await ensureFcmReady())
      if (!token) return false

      const res = await fcmSubscribe(token, topic, user.value)
      if (!res) return false

      if (!activeSubscriptions.has(topic)) {
        activeSubscriptions.add(topic)
      }

      return true
    } catch (error) {
      log.error(`Error subscribing to topic ${topic}:`, error)
      devMode && notifyService.error(`**DEV MSG** Failed to subscribe to ${topic} notifications`)
      return false
    }
  }

  async function unsubscribeFromTopic(topic) {
    try {
      const token = notificationToken.value || (await ensureFcmReady())
      if (!token) return false

      await fcmUnsubscribe(token, topic, user.value)

      if (activeSubscriptions.has(topic)) {
        activeSubscriptions.delete(topic)
      }
      return true
    } catch (error) {
      log.error(`Error unsubscribing from topic ${topic}:`, error)
      devMode &&
        notifyService.error(`**DEV MSG** Failed to unsubscribe from ${topic} notifications`)
      return false
    }
  }

  async function handleCompanyEditNotification(data) {
    if (data.sentById && user.value?.id === data.sentById) {
      return
    }

    const companyId = data.companyId
    const registeredCompany = queryRegistry.companies[companyId]
    let companyName = 'Compny'

    if (registeredCompany) {
      companyName = registeredCompany.data.value?.info?.name
    }
    if (!companyName) {
      const company = await notificationService.getCompanyData(companyId)
      companyName = company?.info?.name || 'Company'
    }

    const targetPath = `/${ROUTES.COMPANY}/${companyId}/${route.params.tab || COMPANY_DETAILS_TABS.INFO}`

    addUpdateNotification({
      type: NOTIFICATION_TYPES.COMPANY_EDIT,
      id: companyId,
      companyId,
      companyName: companyName,
      updatedAt: data.timestamp,
      updatedBy: data.sentByFullName || 'Unknown user',
      path: targetPath,
    })
  }

  async function handleProductEditNotification(data) {
    if (data.sentById && user.value?.id === data.sentById) {
      return
    }

    const productId = data.productId
    const companyId = data.companyId
    const registeredProduct = queryRegistry.products[productId]
    let productName = ''

    if (registeredProduct) {
      productName = registeredProduct.data.value?.name
    }
    if (!productName) {
      const product = await notificationService.getProductData(productId, companyId)
      productName = product?.name || 'Product'
    }

    const targetPath = `/${ROUTES.COMPANY}/${companyId}/${COMPANY_DETAILS_TABS.PRODUCTS}?productId=${productId}`

    addUpdateNotification({
      type: NOTIFICATION_TYPES.PRODUCT_EDIT,
      id: productId,
      companyId: companyId,
      productId: productId,
      productName: productName,
      updatedAt: data.timestamp,
      updatedBy: data.sentByFullName || 'Unknown user',
      path: targetPath,
    })
  }

  async function handleAIAssessmentNotification(data) {
    const isCompanyAssessment = !data.assessmentType.includes('product')
    const isProductAssessment = data.assessmentType.includes('product')
    const isStarting = data.action === 'start'

    let company, product
    let name, targetPath, entityId
    const companyId = data.companyId
    const productId = data.productId

    try {
      company = await notificationService.getCompanyData(companyId)
      if (!company) throw 'Company not found'
      product = company?.products?.find((p) => p.id === productId)
    } catch (err) {
      log.error(`Error getting company or product: ${err}`)
    }

    if (isCompanyAssessment) {
      name = company?.info?.name || 'this company'
      entityId = companyId
      targetPath = `/${ROUTES.COMPANY}/${companyId}/${COMPANY_DETAILS_TABS.AI_ASSESSMENT}`
    } else if (isProductAssessment) {
      name = product?.name || 'this product'
      entityId = productId
      targetPath = `/${ROUTES.COMPANY}/${companyId}/${COMPANY_DETAILS_TABS.PRODUCTS}?productId=${productId}`
    }

    const isViewingCompanyAssessment =
      isCompanyAssessment &&
      route.params.id === companyId &&
      route.params.tab === COMPANY_DETAILS_TABS.AI_ASSESSMENT
    const isViewingProductAssessment =
      isProductAssessment && route.params.id === companyId && route.query.productId === productId

    if (!isStarting) {
      if (isViewingCompanyAssessment) {
        const companyQuery = queryRegistry.companies[companyId]
        if (companyQuery) {
          companyQuery.query.refetch()
        } else {
          notificationService.handleViewingWithoutQuery(router, true)
        }
      } else if (isViewingProductAssessment) {
        const productQuery = queryRegistry.products[productId]
        if (productQuery) {
          productQuery.query.refetch()
        } else {
          notificationService.handleViewingWithoutQuery(router, true)
        }
      }
    }

    addUpdateNotification({
      type: NOTIFICATION_TYPES.AI_ASSESSMENT,
      id: entityId + NOTIFICATION_TYPES.AI_ASSESSMENT,
      companyId: companyId,
      productId: productId,
      companyName: isCompanyAssessment ? name : undefined,
      productName: isProductAssessment ? name : undefined,
      assessmentType: data.assessmentType,
      updatedAt: data.timestamp,
      updatedBy: data.sentByFullName || 'AI System',
      isStarting,
      path: targetPath,
    })
  }

  function handleGenericNotification(data) {
    notifyService.info(`Received notification: ${data.message || 'Test notification received!'}`)
  }

  function handleUserNotification(data) {
    if (data.action === 'end') {
      handleAIAssessmentNotification(data)
    }
  }

  function handleMessage(payload) {
    const data =
      payload.data || (payload.notification && { type: 'notification', ...payload.notification })
    log.info('Received notification:', payload)
    if (!data) return

    switch (data.type) {
      case NOTIFICATION_TYPES.COMPANY_EDIT:
        handleCompanyEditNotification(data)
        break
      case NOTIFICATION_TYPES.PRODUCT_EDIT:
        handleProductEditNotification(data)
        break
      case NOTIFICATION_TYPES.AI_ASSESSMENT: {
        if (data.sentById && data.sentById === user.value?.id) return
        handleAIAssessmentNotification(data)
        break
      }
      case NOTIFICATION_TYPES.USER_NOTIFICATION:
        handleUserNotification(data)
        break
      default:
        handleGenericNotification(data)
        break
    }
  }

  function setupBackgroundMessageListener() {
    if (!('serviceWorker' in navigator)) {
      return null
    }

    const messageHandler = (event) => {
      if (event.data?.type === FCM_MESSAGE_TYPES.BACKGROUND) {
        log.info('[FCM] Legacy background message from service worker')
        handleMessage(event.data.payload)
      }
    }

    navigator.serviceWorker.addEventListener(DOM_EVENTS.MESSAGE, messageHandler)

    return () => {
      navigator.serviceWorker.removeEventListener(DOM_EVENTS.MESSAGE, messageHandler)
    }
  }

  return {
    subscribeToTopic,
    unsubscribeFromTopic,
  }
}

export async function unsubscribeFromAllTopics() {
  return await fcmUnsubscribeAll(notificationToken.value)
}
